import { useAppMessaging, useAuth } from '@idu-nightlife/ui-kit';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  getAppOrigin,
  hasResolvableUrl,
  type AppConfig,
} from '../../shared/components/global-nav/app-config';
import { childToParentPath, parentToChildPath } from './child-path';

/**
 * Shell side of the boot handshake and URL sync with one embedded app.
 * Contract: specs/016-global-nav-venue-sync/contracts/shell-app-messaging.md
 *
 * - The iframe src carries the destination path, so a booting child never has
 *   to be corrected into the right venue (research D1).
 * - `url-change` from the child is discarded until we have answered its
 *   `app-ready` with `init` — a booting child's URL is not authoritative (D2).
 * - Post-init, parent location changes flow down as `navigate` and child
 *   location changes flow up as replace-navigation.
 * - `iframeSrc` is null when the app has no resolvable URL in this deployment;
 *   the caller renders an unavailable state instead of an iframe.
 */
/** The app's own page for a child path; a path the browser reads as another origin (`//host`) opens the app's home instead. */
export function childUrl(appUrl: string, childPath: string): string {
  const url = new URL(childPath, appUrl);
  return url.origin === new URL(appUrl).origin ? url.toString() : new URL('/', appUrl).toString();
}

export function useChildBridge(app: AppConfig): {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  iframeSrc: string | null;
} {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { registerIframe, unregisterIframe, onMessage, sendToChild } = useAppMessaging();
  const { firebaseUser, getAccessToken } = useAuth();

  // Destination the child boots at — computed once per iframe lifecycle.
  const [iframeSrc] = useState(() =>
    hasResolvableUrl(app)
      ? childUrl(app.url, parentToChildPath(app.id, location.pathname, location.search))
      : null,
  );

  const initSentRef = useRef(false);
  const announcedRef = useRef(false);
  // The child path both sides last agreed on; avoids navigate/url-change echo.
  const lastChildPathRef = useRef(parentToChildPath(app.id, location.pathname, location.search));

  useEffect(() => {
    if (iframeRef.current) {
      registerIframe(app.id, iframeRef.current, getAppOrigin(app));
    }

    return () => unregisterIframe(app.id);
  }, [registerIframe, unregisterIframe, app]);

  const sendInit = useCallback(() => {
    const token = getAccessToken();
    if (!token || !firebaseUser) {
      return false;
    }

    const path = lastChildPathRef.current;
    sendToChild(app.id, {
      type: 'init',
      payload: {
        auth: { token, user: JSON.parse(JSON.stringify(firebaseUser)) },
        path,
      },
    });
    initSentRef.current = true;
    return true;
  }, [app.id, firebaseUser, getAccessToken, sendToChild]);

  // Handle messages from the child.
  useEffect(() => {
    const unsubscribe = onMessage((message) => {
      if (message.type === 'app-ready') {
        // Refresh the destination in case the shell navigated while the child
        // was loading, then answer with init (idempotent on repeats).
        announcedRef.current = true;
        lastChildPathRef.current = parentToChildPath(app.id, location.pathname, location.search);
        sendInit();
        return;
      }

      if (message.type === 'url-change') {
        if (!initSentRef.current) {
          console.warn(
            `[useChildBridge:${app.id}] Discarding pre-init url-change`,
            message.payload.path,
          );
          return;
        }

        const newChildPath = message.payload.path;
        lastChildPathRef.current = newChildPath;

        const newParentPath = childToParentPath(app.id, newChildPath);
        const currentParentPath = location.pathname + location.search;

        if (newParentPath !== currentParentPath) {
          // Replace, not push — child-driven URL updates must not spam history.
          navigate(newParentPath, { replace: true });
        }
      }
    });

    return unsubscribe;
  }, [onMessage, app.id, sendInit, navigate, location.pathname, location.search]);

  // If the child announced before shell auth was ready (rare — ProtectedRoute
  // normally guarantees auth first), complete the handshake once it is.
  useEffect(() => {
    if (announcedRef.current && !initSentRef.current) {
      sendInit();
    }
  }, [sendInit]);

  // Push shell-driven location changes down to an initialised child.
  useEffect(() => {
    if (!initSentRef.current) {
      return;
    }

    const childPath = parentToChildPath(app.id, location.pathname, location.search);
    if (childPath === lastChildPathRef.current) {
      return;
    }

    lastChildPathRef.current = childPath;
    try {
      sendToChild(app.id, { type: 'navigate', payload: { path: childPath } });
    } catch (error) {
      console.warn(`[useChildBridge:${app.id}] Failed to send navigate to child`, error);
    }
  }, [app.id, location.pathname, location.search, sendToChild]);

  return { iframeRef, iframeSrc };
}
